import { BACKEND_URL } from '../../plugins/axios';
import axios from 'axios';

import { authService } from '../../services/authService';
import { subscriptionService } from '../../services/subscriptionService';

// 2. Add a simple helper to parse the JWT payload
// This avoids adding a large library like jwt-decode
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT', e);
    return null;
  }
}

const state = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  subscription: null
};

const mutations = {
  SET_USER(state, user) {
    state.user = user;
  },
  SET_ACCESS_TOKEN(state, token) {
    state.accessToken = token;
  },
  SET_REFRESH_TOKEN(state, token) {
    state.refreshToken = token;
  },
  SET_AUTH_STATUS(state, status) {
    state.isAuthenticated = status;
  },
  SET_SUBSCRIPTION(state, subscription) {
    state.subscription = subscription;
  },
  CLEAR_AUTH(state) {
    state.user = null;
    state.accessToken = null;
    state.refreshToken = null;
    state.isAuthenticated = false;
    state.subscription = null;
  }
};

const actions = {
  async fetchSubscription({ commit }) {
    try {
      const sub = await subscriptionService.getSubscriptionStatus();
      commit('SET_SUBSCRIPTION', sub);
    } catch (e) {
      console.error('[store.auth] Failed to fetch subscription', e);
      // We don't block the UI, but we could set an error state if needed.
      // For now, subscription remains null.
    }
  },

  async handleAuthResponse({ commit, dispatch }, { accessToken, refreshToken }) {
    // 3. The backend ONLY sends tokens.
    // We will parse the JWT to get the user's email (which is the 'sub' claim)
    const payload = parseJwt(accessToken);
    const user = payload ? { 
      email: payload.sub,
      name: payload.name || payload.email || payload.sub // Try to get name from payload
    } : null;

    commit('SET_USER', user);
    commit('SET_ACCESS_TOKEN', accessToken);
    commit('SET_REFRESH_TOKEN', refreshToken);
    commit('SET_AUTH_STATUS', true);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Fetch subscription
    dispatch('fetchSubscription');
    dispatch('fetchUser');
  },
  async fetchUser({ commit }) {
    try {
      const response = await authService.fetchProfile();
      commit('SET_USER', response.data);
    } catch (e) {
      console.error('Failed to fetch user profile', e);
    }
  },


  async logout({ commit }) {
    try {
      await authService.logout();
    } catch (e) {
      console.error('Logout error', e);
    }
    commit('CLEAR_AUTH');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // The router guard will handle the redirect
  },

  initializeAuth({ commit, dispatch }) {
    const token = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    if (token) {
      const payload = parseJwt(token);
      
      // Check if token is expired
      if (payload && payload.exp && (payload.exp * 1000) < Date.now()) {
        // Token expired, try to refresh or logout
        if (refreshToken) {
          return dispatch('refreshAccessToken').catch(() => dispatch('logout'));
        } else {
          dispatch('logout');
        }
        return Promise.resolve();
      }
      
      // Token is valid, populate user info
      const user = payload ? { 
        email: payload.sub,
        name: payload.name || payload.email || payload.sub // Try to get name from payload
      } : null;

      commit('SET_USER', user);
      commit('SET_ACCESS_TOKEN', token);
      commit('SET_REFRESH_TOKEN', refreshToken);
      commit('SET_AUTH_STATUS', true);
      
      // Fetch subscription
      dispatch('fetchSubscription');
      dispatch('fetchUser');
    }
    return Promise.resolve();
  },

  setTokens({ commit, dispatch }, { accessToken, refreshToken }) {
    // This is used by the refresh logic, let's also update the user
    const payload = parseJwt(accessToken);
    const user = payload ? { 
      email: payload.sub,
      name: payload.name || payload.email || payload.sub // Try to get name from payload
    } : null;

    commit('SET_USER', user);
    commit('SET_ACCESS_TOKEN', accessToken);
    commit('SET_REFRESH_TOKEN', refreshToken);
    commit('SET_AUTH_STATUS', true);
    if (accessToken) localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    
    // Refresh subscription data as well, in case it changed
    dispatch('fetchSubscription');
    dispatch('fetchUser');
  },

  async refreshAccessToken({ state, dispatch }) {
    try {
      const refreshToken = state.refreshToken || localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token available');

      // Use raw axios to avoid interceptor loops
      // NOTE: Using the existing backend endpoint '/auth/refresh-token'
      const response = await axios.post(`${BACKEND_URL}/auth/refresh-token`, { 
        refreshToken 
      });

      // Handle both 'token' (existing backend) and 'accessToken' (proposed) formats
      const { token, accessToken, refreshToken: newRefreshToken } = response.data;
      const finalAccessToken = accessToken || token;

      if (!finalAccessToken) {
        throw new Error('No access token returned from refresh');
      }

      // Store the new tokens
      await dispatch('setTokens', {
        accessToken: finalAccessToken,
        refreshToken: newRefreshToken || refreshToken
      });

      return finalAccessToken;
    } catch (err) {
      console.error('[auth] Refresh token failed', err);
      dispatch('logout');
      throw err;
    }
  }
};

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  user: state => state.user,
  accessToken: state => state.accessToken,
  refreshToken: state => state.refreshToken,
  subscription: state => state.subscription,
  isAdmin: state => state.user?.roles?.includes('ROLE_ADMIN') || false,
  isTrader: state => state.subscription?.tier === 'TRADER',
  isPro: state => state.subscription?.tier === 'PRO',
  portfoliosLimit: state => {
    if (state.subscription?.tier === 'TRADER') return Infinity;
    if (state.subscription?.tier === 'PRO') return 10;
    return 1; // BASIC
  },
  liabilitiesLimit: state => {
    if (state.subscription?.tier === 'TRADER') return Infinity;
    if (state.subscription?.tier === 'PRO') return 10;
    return 1; // BASIC
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
